/**
 * App.tsx — Root router for the ARIA Training Portal.
 *
 * Route tree (base: /app):
 *
 *   /login                         → LoginPage              (public)
 *   /                              → ProtectedRoute          (auth + role guard)
 *     /                            → AppShell               (sidebar layout)
 *       /                          → redirect /home
 *       /home                      → DashboardPage          (landing dashboard)
 *       /scenarios                 → ScenarioSelectPage
 *       /session/:sessionId        → SessionPage
 *       /session/:sessionId/summary → SessionSummaryPage
 *       /learn                     → LearnPage (my_learning tab)
 *       /catalog                   → LearnPage (catalog tab)
 *       /learn/:enrollmentId       → CourseDetailPage
 *       /history                   → HistoryPage            (EPIC-04-05)
 *       /policy-lab                → PolicyLabPage          (EPIC-13)
 *       /tutor/:courseId/:moduleId → AiTutorPage            (EPIC-08-11)
 *       /drill/today               → DrillTodayPage         (EPIC-08-12)
 *       /profile                   → ProfilePage
 *   /*                             → redirect /home
 *
 * CloudFront delivers the built app at /app/* (see ADR-007, ADR-009).
 * BrowserRouter basename="/app" ensures all React Router <Link> and
 * navigate() calls resolve within that prefix.
 *
 * Auth rules (ADR-007):
 *   - `agent` role:   Training Portal only
 *   - `manager` role: Training Portal + Admin Portal (read)
 *   - `admin` role:   Admin Portal only — redirected to /login here
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppShell } from './components/AppShell';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { ScenarioSelectPage } from './pages/ScenarioSelectPage';
import { SessionPage } from './pages/SessionPage';
import { SessionSummaryPage } from './pages/SessionSummaryPage';
import { HistoryPage } from './pages/HistoryPage';
import { LearnPage } from './pages/LearnPage';
import { CourseDetailPage } from './pages/CourseDetailPage';
import { CourseFeedbackPage } from './pages/CourseFeedbackPage';
import { ReadingPage } from './pages/ReadingPage';
import { QuizPage } from './pages/QuizPage';
import { PolicyLabPage } from './pages/PolicyLabPage';
import { AiTutorPage } from './pages/AiTutorPage';
import { AiAssistantPage } from './pages/AiAssistantPage';
import { DrillTodayPage } from './pages/DrillTodayPage';
import { ProfilePage } from './pages/ProfilePage';

const routerBase =
  import.meta.env.BASE_URL === '/'
    ? '/'
    : import.meta.env.BASE_URL.replace(/\/$/, '');

export default function App(): JSX.Element {
  return (
    <BrowserRouter
      basename={routerBase}
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <Routes>
        {/* Public */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />

        {/* Protected: auth + role guard → sidebar layout → pages */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppShell />}>

            {/* Home dashboard */}
            <Route path="/home" element={<DashboardPage />} />

            {/* Voice roleplay */}
            <Route path="/scenarios" element={<ScenarioSelectPage />} />
            <Route path="/session/:sessionId" element={<SessionPage />} />
            <Route path="/session/:sessionId/summary" element={<SessionSummaryPage />} />

            {/* Score history */}
            <Route path="/history" element={<HistoryPage />} />

            {/* LMS: My Learning + Catalogue */}
            <Route path="/learn" element={<LearnPage />} />
            <Route path="/catalog" element={<LearnPage />} />
            <Route path="/learn/:enrollmentId" element={<CourseDetailPage />} />
            <Route path="/courses/:courseId/feedback" element={<CourseFeedbackPage />} />

            {/* LMS module players (EPIC-08-02 quiz, EPIC-08-03 reading) */}
            <Route path="/reading/:moduleId" element={<ReadingPage />} />
            <Route path="/quiz/:moduleId" element={<QuizPage />} />

            {/* EPIC-13: Policy Document Lab */}
            <Route path="/policy-lab" element={<PolicyLabPage />} />

            {/* EPIC-08-11: AI Tutor Mode */}
            <Route path="/tutor/:courseId/:moduleId" element={<AiTutorPage />} />

            {/* EPIC-08-12: Daily Drill */}
            <Route path="/drill/today" element={<DrillTodayPage />} />

            {/* EPIC-08-16: In-portal AI Assistant */}
            <Route path="/assistant" element={<AiAssistantPage />} />

            {/* Agent Profile */}
            <Route path="/profile" element={<ProfilePage />} />
          </Route>
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
